import { useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_DELAY_MS = 300;
const DEFAULT_MIN_VISIBLE_MS = 600;

/**
 * Debounces the loading INDICATOR, not the loading state itself: a caller's
 * `status` still flips to `'loading'` the instant a request starts, but this
 * only turns true once it has stayed `'loading'` for `delayMs`. A request that
 * resolves inside the window never shows `LoadingState` at all.
 *
 * Once shown, it STAYS shown for at least `minVisibleMs`, even if the request
 * lands a moment later. A full-screen illustration that appears and vanishes
 * inside a few frames reads as a glitch — the learner sees an instrument blink
 * past and cannot tell a loader from an error. Either the screen goes straight
 * to its content, or the loader is on long enough to be read as one.
 *
 * While this is true the caller must keep rendering the loader, even if its
 * own data has already arrived (`useAsyncData` folds that in for you).
 */
export function useDelayedLoading(
  loading: boolean,
  delayMs = DEFAULT_DELAY_MS,
  minVisibleMs = DEFAULT_MIN_VISIBLE_MS,
): boolean {
  const [show, setShow] = useState(false);
  const shownAt = useRef(0);

  // Layout effect so the frame where the hold expires is never painted with
  // the loader AND the content both deciding who owns the screen.
  useLayoutEffect(() => {
    if (loading) {
      if (show) return;
      const timer = window.setTimeout(() => {
        shownAt.current = performance.now();
        setShow(true);
      }, delayMs);
      return () => window.clearTimeout(timer);
    }

    if (!show) return;
    const remaining = minVisibleMs - (performance.now() - shownAt.current);
    if (remaining <= 0) {
      setShow(false);
      return;
    }
    const timer = window.setTimeout(() => setShow(false), remaining);
    return () => window.clearTimeout(timer);
  }, [loading, show, delayMs, minVisibleMs]);

  return show;
}
